import React from 'react';
import { CVData } from '../../types';
import { UserCircle } from 'lucide-react';

interface BaseTemplateProps {
  data: CVData;
  colors: {
    sidebar: string;
    accent: string;
    accentBg: string;
    sidebarFrom?: string;
    sidebarTo?: string;
    accentColor: string;
  };
  isTemplate1: boolean;
  renderMainContent: (experienceItems: React.ReactNode, pageNumber: number) => React.ReactNode;
  darkMode?: boolean;
  sidebarStyle?: string;
}

export function BaseTemplate({
  data,
  colors,
  isTemplate1,
  renderMainContent,
  darkMode = false,
  sidebarStyle = ''
}: BaseTemplateProps) {
  const hasExperience = data.experience && data.experience.length > 0;
  const hasCourses = data.courses && data.courses.length > 0;
  const isContinuationPage = (data as any).isContinuationPage === true;

  // Logs para depuración
  console.log('BaseTemplate - Continuación:', isContinuationPage);
  console.log('BaseTemplate - Experiencias:', data.experience?.length || 0);
  console.log('BaseTemplate - Cursos:', data.courses?.length || 0);
  console.log('BaseTemplate - Colores recibidos:', colors); // Agregamos log para verificar colores
  console.log('BaseTemplate - accentColor:', colors.accentColor);
  console.log('BaseTemplate - sidebarFrom:', colors.sidebarFrom);
  console.log('BaseTemplate - sidebarTo:', colors.sidebarTo);

  // Inicializamos courses como un arreglo vacío si no existe
  if (!data.courses) {
    data.courses = [];
  }

  // --- Mover la definición de renderExperienceItem AQUÍ --- 
  const renderExperienceItem = (exp: typeof data.experience[0], index: number) => (
    <div>
      <h4 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{exp.title}</h4>
      <p className="text-sm" style={{ color: colors.accentColor }}>{exp.company} | {exp.period}</p>
      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} leading-relaxed mt-1 mb-1`}>
        {exp.description}
      </p>
      {exp.achievements && exp.achievements.length > 0 && (
        <div className="mb-1">
          <ul className={`list-disc pl-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {exp.achievements.map((achievement, idx) => (
              <li key={`achievement-${index}-${idx}`} className="mb-0.5">{achievement}</li>
            ))}
          </ul>
        </div>
      )}
      {exp.technologies && exp.technologies.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {exp.technologies.map((tech, idx) => (
            <span
              key={`tech-${index}-${idx}`}
              className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-amber-500/20 text-amber-300' : 'text-white'
                }`}
              style={{ backgroundColor: darkMode ? undefined : colors.accentColor }}
            >
              {tech}
            </span>
          ))}
        </div>
      )}
    </div>
  );
  // --- FIN de la definición movida ---

  // Ahora podemos usar renderExperienceItem
  const experienceItems = (data.experience || []).map((exp, index) => renderExperienceItem(exp, index));

  const renderContactInfo = () => (
    <div className="space-y-2">
      {data.contact.phone && (
        <div className={`flex items-center gap-2 text-sm ${darkMode ? 'text-gray-300' : isTemplate1 ? 'text-white' : 'text-gray-700'}`}>
          <svg className={`w-4 h-4 shrink-0 ${isTemplate1 ? 'text-white' : ''}`} style={{ color: isTemplate1 ? undefined : colors.accentColor }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
          <span className="break-all">{data.contact.phone}</span>
        </div>
      )}
      {data.contact.email && (
        <div className={`flex items-center gap-2 text-sm ${darkMode ? 'text-gray-300' : isTemplate1 ? 'text-white' : 'text-gray-700'}`}>
          <svg className={`w-4 h-4 shrink-0 ${isTemplate1 ? 'text-white' : ''}`} style={{ color: isTemplate1 ? undefined : colors.accentColor }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          <span className="break-all">{data.contact.email}</span>
        </div>
      )}
      {data.contact.location && (
        <div className={`flex items-center gap-2 text-sm ${darkMode ? 'text-gray-300' : isTemplate1 ? 'text-white' : 'text-gray-700'}`}>
          <svg className={`w-4 h-4 shrink-0 ${isTemplate1 ? 'text-white' : ''}`} style={{ color: isTemplate1 ? undefined : colors.accentColor }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span className="break-all">{data.contact.location}</span>
        </div>
      )}
    </div>
  );

  const renderPhoto = () => {
    // Si no hay foto, no renderizar nada
    if (!data.photo) {
      return null;
    }

    return (
      <div className="relative mx-auto mb-4" style={{ width: '144px', height: '144px' }}>
        {/* Borde exterior con color del template */}
        <div
          className="rounded-full shadow-lg"
          style={{
            width: '144px',
            height: '144px',
            padding: '5px',
            backgroundColor: `${colors.sidebarFrom}70`,
            position: 'relative'
          }}
        >
          {/* Contenedor de la foto */}
          <div
            className="rounded-full overflow-hidden"
            style={{
              width: '134px',
              height: '134px',
              border: `4px solid ${colors.sidebarTo}30`,
              position: 'relative'
            }}
          >
            <img
              src={data.photo}
              alt="Foto de perfil"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
                borderRadius: '50%'
              }}
              crossOrigin="anonymous"
              onError={(e) => {
                // Si hay error cargando la imagen, ocultar el elemento
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderSidebar = () => (
    <>
      {/* Foto y presentación */}
      <div className="p-4">
        {renderPhoto()}
        <div className={`mt-4 p-4 rounded-xl ${sidebarStyle} ${darkMode
            ? 'bg-zinc-800/50 text-gray-300'
            : isTemplate1
              ? 'bg-white/10 backdrop-blur-sm text-white'
              : 'bg-white/90 text-gray-700'
          }`}>
          <h3 className={`font-bold mb-3 text-lg ${!darkMode && isTemplate1 ? 'text-white' : ''
            }`} style={{ color: darkMode || !isTemplate1 ? colors.accentColor : undefined }}>
            Sobre Mí
          </h3>
          <p className="text-sm leading-relaxed">
            {data.about}
          </p>
        </div>
      </div>

      {/* Contacto */}
      <div className={`p-4 mx-4 mb-4 ${sidebarStyle} ${darkMode
          ? 'bg-zinc-800/50'
          : isTemplate1
            ? 'bg-white/10 backdrop-blur-sm'
            : 'bg-white/90'
        } rounded-xl`}>
        <h3 className={`font-bold mb-3 text-lg ${!darkMode && isTemplate1 ? 'text-white' : ''
          }`} style={{ color: darkMode || !isTemplate1 ? colors.accentColor : undefined }}>
          Contacto
        </h3>
        {renderContactInfo()}
      </div>

      {/* Habilidades */}
      <div className={`p-4 mx-4 mb-4 ${sidebarStyle} ${darkMode
          ? 'bg-zinc-800/50'
          : isTemplate1
            ? 'bg-white/10 backdrop-blur-sm'
            : 'bg-white/90'
        } rounded-xl`}>
        <h3 className={`font-bold mb-3 text-lg ${!darkMode && isTemplate1 ? 'text-white' : ''
          }`} style={{ color: darkMode || !isTemplate1 ? colors.accentColor : undefined }}>
          Habilidades
        </h3>
        <div className="flex flex-wrap gap-1.5 items-start justify-start">
          {data.skills.slice(0, 8).map((skill, index) => {
            // Determinar el tamaño basado en la longitud de la habilidad
            let sizeClass = "";
            let extraPaddingClass = "";

            if (skill.length <= 3) {
              // Habilidades extra cortas (ej: "SQL", "CSS", "Git")
              sizeClass = "flex-initial";
              extraPaddingClass = "px-3";
            } else if (skill.length <= 8) {
              // Habilidades cortas (ej: "Python", "React JS")
              sizeClass = "flex-initial";
              extraPaddingClass = "px-2.5";
            } else if (skill.length <= 12) {
              // Habilidades medianas (ej: "Microsoft Excel")
              sizeClass = "min-w-[45%]";
              extraPaddingClass = "px-2";
            } else {
              // Habilidades largas
              sizeClass = "w-full";
              extraPaddingClass = "px-2";
            }

            // Propiedades de estilo para colores dinámicos
            const skillStyle = darkMode
              ? { backgroundColor: colors.accentColor + '20', color: '#ffffff' }
              : isTemplate1
                ? { backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }
                : { backgroundColor: colors.sidebarFrom + '20', color: colors.accentColor };

            return (
              <span
                key={`skill-${index}`}
                className={`inline-block text-xs font-medium py-1.5 rounded-md shadow-sm ${extraPaddingClass} text-center ${sizeClass}`}
                style={skillStyle}
                title={skill}
              >
                {skill}
              </span>
            );
          })}
        </div>
      </div>

      {/* Idiomas */}
      <div className={`p-4 mx-4 ${sidebarStyle} ${darkMode
          ? 'bg-zinc-800/50'
          : isTemplate1
            ? 'bg-white/10 backdrop-blur-sm'
            : 'bg-white/90'
        } rounded-xl`}>
        <h3 className={`font-bold mb-3 text-lg ${!darkMode && isTemplate1 ? 'text-white' : ''
          }`} style={{ color: darkMode || !isTemplate1 ? colors.accentColor : undefined }}>
          Idiomas
        </h3>
        <div className="space-y-2">
          {data.languages.map((lang, index) => (
            <div key={`lang-${index}`} className={`flex justify-between items-center ${darkMode ? 'text-gray-300' : isTemplate1 ? 'text-white' : 'text-gray-700'
              }`}>
              <div className="font-medium text-xs">{lang.name}</div>
              <div className={`text-xs px-2 py-0.5 rounded ${isTemplate1
                  ? 'bg-white/20 backdrop-blur-sm text-white'
                  : 'bg-gray-200 text-gray-700'
                }`} style={darkMode ? { backgroundColor: colors.accentColor + '20', color: '#ffffff' } : undefined}>
                {lang.level}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  // ÚNICA función para renderizar la página
  const renderPage = () => {
    console.log(`BaseTemplate - Renderizando página (Continuation: ${isContinuationPage})`);

    return (
      <div className={`cv-preview relative ${darkMode ? 'bg-gradient-to-br from-zinc-900 to-black' : 'bg-gradient-to-br from-blue-50 to-white'}`}>
        {/* Barra lateral - SIEMPRE VISIBLE */}
        {/* {!isContinuationPage && ( */}
        <div
          className="absolute h-full"
          style={{
            width: '30%',
            background: `linear-gradient(to bottom right, ${colors.sidebarFrom || '#4B6A9B'}, ${colors.sidebarTo || '#364F6B'})`
          }}
        >
          {renderSidebar()}
        </div>
        {/* )} */}

        {/* Contenido principal - SIEMPRE 70% */}
        <div className={`absolute top-0 bottom-0 p-6 w-[70%] right-0`}>
          {/* ${isContinuationPage ? 'w-full left-0' : 'w-[70%] right-0'} */}
          {renderMainContent(experienceItems, isContinuationPage ? 2 : 1)}
        </div>
      </div>
    );
  };

  // Retorna directamente el resultado de renderPage()
  return renderPage();
}
